import React, { useEffect, useRef } from "react";
import gsap from "gsap";

const COLORS = {
  primary: "#3AAFA9",
  secondary: "#7BC8A4",
  heading: "#2E2E2E",
  text: "#4B5563",
  sectionTitle: "#1F7A75",
  background: "#3AAFA9",
};

const shippingSections = [
  {
    title: "1. Service Delivery",
    text:
      "JANAK ELDERBRIDGE PRIVATE LIMITED provides elder care, health monitoring, and related support services directly to the requested service address. Since our offerings are service-based (in-person visits), this policy outlines how those services are delivered.",
  },
  {
    title: "2. Service Area Coverage",
    text:
      "Our services are currently available in select cities and regions. You can verify coverage by entering your address or PIN code during the registration or booking process in the Janak App.",
  },
  {
    title: "3. Scheduling and Time Slots",
    list: [
      "Services must be booked for specific time slots available in the app (e.g., 08:00 AM - 10:00 AM).",
      "We recommend booking at least 24 hours in advance to ensure the availability of a preferred care partner.",
      "Scheduled care partner visits are dispatched as per your confirmed booking time slot."
    ]
  },
  {
    title: "4. Partner Assignment",
    text:
      "Once a booking is confirmed and payment is successful, a verified Janak Care Partner will be assigned to your request. You can track their status and ETA directly through the app on the day of the service.",
  },
  {
    title: "5. Delays and Rescheduling",
    list: [
      "While our care partners strive to arrive on time, unforeseen circumstances (e.g., severe traffic, weather conditions) may cause slight delays.",
      "If a delay exceeds 30 minutes, you will be notified, and you may choose to reschedule the service without any penalty.",
      "If you need to reschedule a service, please do so at least 12 hours in advance via the app."
    ]
  },
  {
    title: "6. Service Fulfillment",
    text:
      "A service is considered 'delivered' or 'fulfilled' when the assigned care partner completes the requested tasks at the designated location and submits the 'Visit Proof' (e.g., photos or reports) via their app, which you can then view in your dashboard.",
  },
  {
    title: "7. Contact Information",
    text: (
      <span>
        If you face any issues with service delivery or partner arrival, please contact our support team immediately at{" "}
        <a href="mailto:contact@thejanak.com" className="text-[#3AAFA9] hover:underline font-medium">
          contact@thejanak.com
        </a>{" "}
        or call{" "}
        <a href="tel:+919098114568" className="text-[#3AAFA9] hover:underline font-medium">
          +91 90981 14568
        </a>.
      </span>
    ),
  },
];

const ShippingDelivery = () => {
  const sectionRef = useRef(null);
  const cardRef = useRef(null);
  const headingRef = useRef(null);
  const introRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({ defaults: { ease: "power2.out" } });
      tl.from(sectionRef.current, { opacity: 0, duration: 0.5 })
        .from(cardRef.current, { y: 36, opacity: 0, duration: 0.7 }, "-=0.2")
        .from(headingRef.current, { y: 18, opacity: 0, duration: 0.45 }, "-=0.2")
        .from(introRef.current, { y: 16, opacity: 0, duration: 0.45 }, "-=0.25")
        .from(
          ".shipping-section-item",
          { y: 24, opacity: 0, stagger: 0.07, duration: 0.38 },
          "-=0.2",
        );
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative min-h-screen overflow-hidden px-6 py-20"
      style={{ background: COLORS.background }}
    >
      <div
        className="absolute left-10 top-24 h-72 w-72 rounded-full blur-3xl"
        style={{ background: `${COLORS.primary}33` }}
      />
      <div
        className="absolute bottom-20 right-10 h-72 w-72 rounded-full blur-3xl"
        style={{ background: `${COLORS.secondary}40` }}
      />

      <div
        ref={cardRef}
        className="relative mx-auto max-w-4xl rounded-3xl bg-white/80 px-6 py-12 text-gray-800 shadow-[0_30px_90px_rgba(0,0,0,0.15)] backdrop-blur-2xl md:px-12"
      >
        <h1
          ref={headingRef}
          className="mb-10 text-center text-4xl font-extrabold md:text-5xl"
          style={{ color: COLORS.heading }}
        >
          Shipping & Delivery Policy
        </h1>

        <p
          ref={introRef}
          className="mb-10 text-center text-base leading-relaxed md:text-lg"
          style={{ color: COLORS.text }}
        >
          This policy outlines how in-person elder care services are scheduled, fulfilled, and delivered to your specified address.
        </p>

        <div className="space-y-9 text-[15.5px] leading-relaxed">
          {shippingSections.map((section) => (
            <div key={section.title} className="shipping-section-item">
              <h2
                className="mb-3 text-xl font-bold md:text-2xl"
                style={{ color: COLORS.sectionTitle }}
              >
                {section.title}
              </h2>
              {section.text ? <p style={{ color: COLORS.text }}>{section.text}</p> : null}
              {section.list ? (
                <ul className="list-disc space-y-1.5 pl-6" style={{ color: COLORS.text }}>
                  {section.list.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              ) : null}
            </div>
          ))}
        </div>

        <p className="mt-14 text-center text-sm text-gray-500">Last updated: July 2026</p>
      </div>
    </section>
  );
};

export default ShippingDelivery;
